import { useState, useCallback } from "react";
import { Plus, Check, Copy, User, MessageSquare, BookOpen, Bot, ChevronDown, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from 'sonner';
import { useAgents } from "@/hooks/useAgents";
import { usePrompts } from "@/hooks/usePrompts";
import { useProjects } from "@/hooks/useProjects";
import { useNavigation } from "@/contexts/NavigationContext";
import { FavoriteButton } from "@/components/FavoriteButton";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ProjectFormModal } from "@/components/modals/ProjectFormModal";
import { SidebarNavItem } from "./SidebarNavItem";
import { ConversationList } from "./ConversationList";
import type { NavTab } from "@/types/navigation";

const iconClass = "w-3.5 h-3.5 shrink-0";
const navIconMap: Record<string, React.ReactNode> = {
  chat: <MessageSquare className={iconClass} />,
  prompts: <BookOpen className={iconClass} />,
  agents: <Bot className={iconClass} />,
};

const projectNavItems: { icon: React.ReactNode; label: string; tab: NavTab }[] = [
  { icon: navIconMap.chat, label: "Chat", tab: "chat" },
  { icon: navIconMap.prompts, label: "Prompts", tab: "prompts" },
  { icon: navIconMap.agents, label: "Agentes", tab: "agents" },
];

export const SidebarSectionPersonal = () => {
  const {
    navigation,
    navigateToPersonal,
    navigateToProject,
    navigateToTab,
    activeConversationId,
    setActiveConversationId,
    activeAgentId,
    setActiveAgentId,
    setEditingResource,
  } = useNavigation();
  

  const { data: personalAgents = [] } = useAgents("personal");
  const { data: personalPrompts = [] } = usePrompts("personal");
  const { data: personalProjects, isLoading: projectsLoading } = useProjects("personal");

  const [personalModalOpen, setPersonalModalOpen] = useState(false);
  const [copiedPromptId, setCopiedPromptId] = useState<string | null>(null);

  const handleCopyPrompt = useCallback(async (prompt: { id: string; name: string; content: string }) => {
    await navigator.clipboard.writeText(prompt.content);
    setCopiedPromptId(prompt.id);
    toast.success(`"${prompt.name}" copiado para a área de transferência`);
    setTimeout(() => setCopiedPromptId(null), 1500);
  }, [toast]);

  const personalScopeAgents = personalAgents.filter((a) => a.scope_type === "personal");
  const personalScopePrompts = personalPrompts.filter((p) => p.scope_type === "personal");

  const isPersonalTabActive = (tab: NavTab) => navigation.isPersonal && !navigation.projectId && navigation.tab === tab;
  const isProjectTabActive = (projectId: string, tab: NavTab) =>
    navigation.isPersonal && navigation.projectId === projectId && navigation.tab === tab;

  const handleSelectAgent = (agentId: string) => {
    setActiveAgentId(agentId);
    setActiveConversationId(null);
    navigateToTab("chat");
  };

  return (
    <>
      <AccordionItem
        value="pessoal"
        className={cn(
          "border-none transition-all",
          navigation.isPersonal && !navigation.projectId ? "bg-surface border border-brand-primary/40 rounded-lg shadow-sm p-1" : "",
        )}
      >
        <AccordionTrigger className="text-xs font-medium text-tertiary uppercase px-3 py-1.5 hover:no-underline hover:bg-hover rounded-md flex-row-reverse justify-end gap-1.5 [&>svg]:text-tertiary [&>svg]:w-3 [&>svg]:h-3">
          <span className="flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" /> Pessoal
          </span>
        </AccordionTrigger>
        <AccordionContent className="pb-0 pt-1">
          <div className="space-y-0.5 pl-4">
            <SidebarNavItem
              icon={navIconMap.chat}
              label="Chat"
              tab="chat"
              isActive={isPersonalTabActive("chat")}
              onClick={() => { navigateToPersonal(); navigateToTab("chat"); setActiveConversationId(null); }}
            />

            <SidebarNavItem
              icon={navIconMap.prompts}
              label="Meus Prompts"
              tab="prompts"
              isActive={isPersonalTabActive("prompts")}
              onClick={() => { navigateToPersonal(); navigateToTab("prompts"); }}
            >
              {personalScopePrompts.length > 0 && (
                <div className="space-y-0.5 py-0.5">
                  {personalScopePrompts.map((prompt) => (
                    <button
                      key={prompt.id}
                      onClick={(e) => { e.stopPropagation(); handleCopyPrompt(prompt); }}
                      className="w-full flex items-center gap-2 px-3 py-1 text-[11px] text-tertiary hover:text-secondary hover:bg-hover rounded-md transition-colors truncate"
                    >
                      {copiedPromptId === prompt.id ? (
                        <Check className="w-3 h-3 text-brand-primary shrink-0 animate-scale-in" />
                      ) : (
                        <Copy className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100" />
                      )}
                      <span className={cn("truncate", copiedPromptId === prompt.id && "text-brand-primary")}>
                        {prompt.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </SidebarNavItem>

            <SidebarNavItem
              icon={navIconMap.agents}
              label="Meus Agentes"
              tab="agents"
              isActive={isPersonalTabActive("agents")}
              onClick={() => { navigateToPersonal(); navigateToTab("agents"); }}
            >
              {personalScopeAgents.length > 0 && (
                <div className="space-y-0.5 py-0.5">
                  {personalScopeAgents.map((agent) => (
                    <button
                      key={agent.id}
                      onClick={() => { navigateToPersonal(); handleSelectAgent(agent.id); }}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-1 text-[11px] rounded-md transition-colors truncate",
                        activeAgentId === agent.id
                          ? "text-brand-primary font-medium"
                          : "text-tertiary hover:text-secondary hover:bg-hover",
                      )}
                    >
                      <span className="truncate">{agent.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </SidebarNavItem>
          </div>

          {/* Projetos pessoais */}
          <div className="mt-2">
            {projectsLoading ? (
              <div className="space-y-1 px-3">
                {Array.from({ length: 2 }).map((_, i) => (
                  <Skeleton key={i} className="h-7 w-full rounded-md" />
                ))}
              </div>
            ) : personalProjects && personalProjects.length > 0 ? (
              <div className="space-y-0.5">
                {personalProjects.map((project) => {
                  const isActive = navigation.projectId === project.id && navigation.isPersonal;
                  return (
                    <Collapsible key={project.id} open={isActive || undefined}>
                      <div
                        className={cn(
                          "transition-all",
                          isActive ? "bg-surface border border-brand-primary/40 rounded-lg shadow-sm p-1" : "",
                        )}
                      >
                        <div className="group/project flex items-center">
                          <CollapsibleTrigger className="group flex-1 min-w-0 flex items-center gap-2 px-3 py-1.5 text-xs text-secondary hover:bg-hover hover:text-primary rounded-md transition-colors">
                            <ChevronDown className="w-3 h-3 text-tertiary transition-transform duration-200 group-data-[state=closed]:-rotate-90" />
                            <span className="flex-shrink-0 text-sm">{project.icon}</span>
                            <span className="flex-1 text-left truncate">{project.name}</span>
                          </CollapsibleTrigger>
                          <div className="flex items-center gap-0.5 opacity-0 group-hover/project:opacity-100 transition-opacity pr-1">
                            <FavoriteButton targetId={project.id} targetType="project" size={12} className="p-0.5" />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToProject(project.id, true);
                                setEditingResource({ type: 'project-settings', id: project.id });
                              }}
                              className="p-0.5 text-tertiary hover:text-primary transition-colors"
                              title="Configurações"
                            >
                              <Settings className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <CollapsibleContent className="pl-6">
                          <div className="space-y-0.5 py-0.5">
                            {projectNavItems.map((item) => (
                              <div key={item.tab}>
                                <button
                                  onClick={() => { navigateToProject(project.id, true); navigateToTab(item.tab); if (item.tab === 'chat') setActiveConversationId(null); }}
                                  className={cn(
                                    "w-full flex items-center gap-2 px-3 py-1 text-xs rounded-md transition-colors",
                                    isProjectTabActive(project.id, item.tab)
                                      ? "text-primary font-medium"
                                      : "text-secondary hover:bg-hover hover:text-primary",
                                  )}
                                  style={
                                    isProjectTabActive(project.id, item.tab)
                                      ? { backgroundColor: "hsl(var(--brand-primary) / 0.10)" }
                                      : undefined
                                  }
                                >
                                  {item.icon}
                                  <span>{item.label}</span>
                                </button>
                                {item.tab === "chat" && isActive && navigation.tab === "chat" && (
                                  <ConversationList
                                    projectId={project.id}
                                    activeConversationId={activeConversationId}
                                    setActiveConversationId={setActiveConversationId}
                                    navigateToTab={navigateToTab}
                                    useGreenHighlight={true}
                                    onBeforeSelect={(conv: any) => {
                                      setActiveAgentId(conv?.agent_id ?? null);
                                    }}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
              </div>
            ) : null}

            <button
              onClick={() => setPersonalModalOpen(true)}
              className="w-full flex items-center gap-2 px-3 py-1.5 mt-1 text-[11px] text-tertiary hover:text-secondary hover:bg-hover rounded-md transition-colors"
            >
              <Plus className="w-3 h-3" />
              <span>Novo Projeto</span>
            </button>
          </div>
        </AccordionContent>
      </AccordionItem>

      <ProjectFormModal open={personalModalOpen} onOpenChange={setPersonalModalOpen} defaultScope="personal" />
    </>
  );
};
